import { CreditCard as CardIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";

import PoweredByStripe from "common/components/PoweredByStripe";
import { Button } from "common/components/ui/Button";
import { DialogDescription, DialogTitle } from "common/components/ui/Dialog";
import { DrawerDescription, DrawerTitle } from "common/components/ui/Drawer";
import usePaymentSourceMutations from "common/datahooks/usePaymentSourceMutations";
import usePaymentSources from "common/datahooks/usePaymentSources";
import { trackMetaPixel } from "common/helpers/utils";
import useGTM from "common/hooks/useGTM";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { PaymentSource } from "common/types";

import CreditCard from "./CreditCard";
import PaypalCheckout from "./PaypalCheckout";

interface AddPaymentMethodProps {
  subscriptionId: string;
  onBack: () => void;
  onNext?: (newPaymentSource: PaymentSource) => void;
}

export default function AddPaymentMethod({
  subscriptionId,
  onBack,
  onNext = undefined,
}: AddPaymentMethodProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [isAddingCard, setIsAddingCard] = useState(false);
  const { setPaymentSource, isSettingPaymentSource } =
    usePaymentSourceMutations();

  const { addPaymentSource, isAddingPaymentSource } = usePaymentSources();

  const { sendEvent } = useGTM();

  useEffect(() => {
    let isCbInitialized: boolean;
    try {
      isCbInitialized = !!window.Chargebee.getInstance();
    } catch (_err) {
      isCbInitialized = false;
    }

    if (window.Chargebee && !isCbInitialized) {
      window.Chargebee.init({
        site: import.meta.env.VITE_CHARGEBEE_SITE,
        publishableKey: import.meta.env.VITE_CHARGEBEE_KEY,
      });
    }
  }, []);

  function onAdd(token: string, source: "card" | "paypal" | "apple-pay") {
    addPaymentSource({ token, source })
      .then((paymentSource) => {
        trackMetaPixel("track", "AddPaymentInfo");
        sendEvent("add_payment_source_success", ({ email }) => ({
          email,
          payment_method: source,
        }));

        if (subscriptionId) {
          setPaymentSource({
            paymentSource,
            subscriptionId,
          }).then(() => {
            if (onNext) {
              onNext(paymentSource);
            } else {
              onBack();
            }
          });
          return;
        }

        onNext?.(paymentSource);
      })
      .catch(() => {
        sendEvent("add_payment_source_fail", ({ email }) => ({
          email,
          payment_method: source,
        }));
      });
  }

  const isLoading = isAddingPaymentSource || isSettingPaymentSource;

  let content = (
    <>
      <div className="flex flex-col gap-3">
        <button
          className="flex w-full items-center justify-center gap-2 rounded-3xl bg-purple-50 py-4 text-sm-bold"
          onClick={() => setIsAddingCard(true)}
          type="button"
        >
          <CardIcon weight="fill" size={20} />
          Credit Card
        </button>

        <PaypalCheckout onTokenReceived={(token) => onAdd(token, "paypal")} />
      </div>
      <PoweredByStripe />

      <Button
        onClick={onBack}
        variant="secondary-black"
        disabled={isLoading}
        className="mr-auto mt-10"
      >
        Back
      </Button>
    </>
  );

  if (isAddingCard) {
    content = (
      <CreditCard
        onTokenReceived={(token) => onAdd(token, "card")}
        onBack={() => setIsAddingCard(false)}
      />
    );
  }

  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <>
      <Title>Add payment method</Title>
      <Description className="mb-8 ">
        Add a preferred payment method for your Aimfox subscription. You can
        change this at any time
      </Description>

      {content}
    </>
  );
}
